import React, { useMemo, useState } from 'react';
import { useAdminOperatingExceptions } from '../../hooks/useAdminOperatingExceptions.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';

const getExceptionLabel = (value) => (value === 'custom' ? 'Horarios personalizados' : 'Disponible 24/7');

const OperatingExceptionsSection = ({ onNotify }) => {
  const {
    items,
    loading,
    error,
    options,
    refresh,
    saveException,
    deleteException
  } = useAdminOperatingExceptions();

  const [search, setSearch] = useState('');
  const [editorState, setEditorState] = useState({ open: false, record: null, saving: false });

  useLucideIcons([items.length, search, editorState.open]);

  const filteredItems = useMemo(() => {
    if (!search) {
      return items;
    }
    const query = search.toLowerCase();
    return items.filter((item) => {
      const sucursal = item.sucursal_nombre?.toLowerCase() || '';
      const producto = item.producto_nombre?.toLowerCase() || '';
      const motivo = item.motivo?.toLowerCase() || '';
      return sucursal.includes(query) || producto.includes(query) || motivo.includes(query);
    });
  }, [items, search]);

  const handleOpenModal = (record = null) => {
    setEditorState({
      open: true,
      saving: false,
      record: record
        ? {
          id: record.id,
          sucursal_ids: record.sucursal_id ? [String(record.sucursal_id)] : [],
          tipo_producto: record.tipo_producto,
          producto_id: record.producto_id,
          tipo_excepcion: record.tipo_excepcion,
          motivo: record.motivo || '',
          notas: record.notas || '',
          activo: record.activo
        }
        : {
          sucursal_ids: [],
          tipo_producto: '',
          producto_id: '',
          tipo_excepcion: '24h',
          motivo: '',
          notas: '',
          activo: true
        }
    });
  };

  const handleSave = async (values) => {
    setEditorState((prev) => ({ ...prev, saving: true }));
    try {
      await saveException(values);
      setEditorState({ open: false, record: null, saving: false });
      refresh();
      onNotify?.({
        type: 'success',
        message: values?.id ? 'Excepción actualizada correctamente.' : 'Excepción creada correctamente.'
      });
    } catch (err) {
      setEditorState((prev) => ({ ...prev, saving: false }));
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo guardar la excepción.' });
    }
  };

  const handleDelete = async (record) => {
    const confirmed = window.confirm(`¿Eliminar la excepción de ${record.producto_nombre}?`);
    if (!confirmed) {
      return;
    }
    try {
      await deleteException(record.id);
      refresh();
      onNotify?.({ type: 'success', message: 'Excepción eliminada.' });
    } catch (err) {
      onNotify?.({ type: 'error', message: err?.message || 'No se pudo eliminar la excepción.' });
    }
  };

  const closeModal = () => {
    setEditorState({ open: false, record: null, saving: false });
  };

  return (
    <section id="operating-exceptions-section" className="admin-section active">
      <div className="section-header">
        <h2 className="section-title">Excepciones de Horarios Operativos</h2>
        <div className="section-actions">
          <div className="search-container">
            <span className="search-icon"><i data-lucide="search" /></span>
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por sucursal, producto o motivo..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search ? (
              <button type="button" className="btn-secondary" onClick={() => setSearch('')}>
                Limpiar
              </button>
            ) : null}
          </div>
          <button className="btn-primary" type="button" onClick={() => handleOpenModal()}>
            <span className="btn-icon"><i data-lucide="plus" /></span>
            Nueva Excepción
          </button>
        </div>
      </div>
      <div className="data-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Cargando excepciones...</div>
          </div>
        ) : (
          <table className="data-table" id="operating-exceptions-table">
            <thead>
              <tr>
                <th>Sucursal</th>
                <th>Producto</th>
                <th>Tipo</th>
                <th>Horario normal</th>
                <th>Excepción</th>
                <th>Motivo</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="alert-triangle" /></div>
                    <div className="empty-state-text">{error}</div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={8} className="empty-state">
                    <div className="empty-state-icon"><i data-lucide="clock-alert" /></div>
                    <div className="empty-state-text">No hay excepciones configuradas.</div>
                    <div className="empty-state-subtext">Crea una nueva excepción para comenzar.</div>
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td>{item.sucursal_nombre}</td>
                    <td>
                      <div className="product-info">
                        <strong>{item.producto_nombre}</strong>
                        <small>{item.tipo_producto === 'article' ? 'Artículo' : 'Receta'}</small>
                      </div>
                    </td>
                    <td>
                      <span className={`type-badge type-${item.tipo_producto}`}>
                        {item.tipo_producto === 'article' ? 'Artículo' : 'Receta'}
                      </span>
                    </td>
                    <td><span className="schedule-info">{item.horario_normal || 'Horarios del producto'}</span></td>
                    <td>
                      <span className={`exception-badge exception-${item.tipo_excepcion}`}>
                        {getExceptionLabel(item.tipo_excepcion)}
                      </span>
                    </td>
                    <td className="motivo-cell">{item.motivo || 'Sin motivo'}</td>
                    <td>
                      <span className={`status-badge ${item.activo ? 'status-active' : 'status-inactive'}`}>
                        {item.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn edit" type="button" onClick={() => handleOpenModal(item)}>
                          <i data-lucide="edit" />
                        </button>
                        <button className="action-btn delete" type="button" onClick={() => handleDelete(item)}>
                          <i data-lucide="trash-2" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {editorState.open ? (
        <OperatingExceptionModal
          state={editorState}
          options={options}
          onClose={closeModal}
          onSubmit={handleSave}
        />
      ) : null}
    </section>
  );
};

const getDefaultProductId = (type, values) => {
  if (type === 'article') {
    return values.producto_id && values.tipo_producto === 'article' ? values.producto_id : '';
  }
  if (type === 'recipe') {
    return values.producto_id && values.tipo_producto === 'recipe' ? values.producto_id : '';
  }
  return '';
};

const OperatingExceptionModal = ({ state, options, onClose, onSubmit }) => {
  const [formValues, setFormValues] = useState(() => ({
    ...state.record,
    sucursal_ids: state.record?.sucursal_ids || [],
    tipo_producto: state.record?.tipo_producto || '',
    producto_id: state.record?.producto_id || '',
    tipo_excepcion: state.record?.tipo_excepcion || '24h',
    motivo: state.record?.motivo || '',
    notas: state.record?.notas || '',
    activo: state.record?.activo ?? true
  }));

  const productType = formValues.tipo_producto;

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSucursalChange = (event) => {
    const { options, multiple, value } = event.target;
    if (!multiple) {
      setFormValues((prev) => ({
        ...prev,
        sucursal_ids: value ? [value] : []
      }));
      return;
    }
    const selected = Array.from(options)
      .filter((option) => option.selected)
      .map((option) => option.value);
    setFormValues((prev) => ({
      ...prev,
      sucursal_ids: selected
    }));
  };

  const handleProductTypeChange = (event) => {
    const nextType = event.target.value;
    setFormValues((prev) => ({
      ...prev,
      tipo_producto: nextType,
      producto_id: getDefaultProductId(nextType, prev)
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      ...formValues,
      sucursal_id: formValues.sucursal_ids?.[0] || '',
      id: state.record?.id || null
    });
  };

  return (
    <div className="modal" style={{ display: 'block' }}>
      <div className="modal-content" style={{ maxWidth: '640px' }}>
        <div className="modal-header">
          <h3>{state.record?.id ? 'Editar Excepción' : 'Nueva Excepción'}</h3>
          <button className="modal-close" type="button" onClick={onClose}>
            <i data-lucide="x" />
          </button>
        </div>
        <form className="modal-body form-grid" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Sucursales</label>
            <select
              className="form-select"
              name="sucursal_ids"
              multiple={!state.record?.id}
              value={state.record?.id ? (formValues.sucursal_ids?.[0] || '') : formValues.sucursal_ids}
              onChange={handleSucursalChange}
              required
            >
              <option value="" disabled={Boolean(state.record?.id)}>
                {state.record?.id ? 'Seleccionar sucursal' : 'Seleccionar sucursales'}
              </option>
              {options.restaurants.map((restaurant) => (
                <option key={restaurant.id} value={restaurant.id}>
                  {restaurant.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Tipo de producto</label>
            <div className="radio-group">
              <label className="radio-item">
                <input
                  type="radio"
                  name="tipo_producto"
                  value="article"
                  checked={productType === 'article'}
                  onChange={handleProductTypeChange}
                />
                <span className="radio-mark" />
                <span className="radio-label">Artículo</span>
              </label>
              <label className="radio-item">
                <input
                  type="radio"
                  name="tipo_producto"
                  value="recipe"
                  checked={productType === 'recipe'}
                  onChange={handleProductTypeChange}
                />
                <span className="radio-mark" />
                <span className="radio-label">Receta</span>
              </label>
            </div>
          </div>

          {productType === 'article' ? (
            <div className="form-group full-width">
              <label className="form-label">Artículo</label>
              <select
                className="form-select"
                name="producto_id"
                value={formValues.producto_id}
                onChange={handleChange}
                required
              >
                <option value="">Seleccionar artículo</option>
                {options.articles.map((article) => (
                  <option key={article.id} value={article.id}>
                    {article.nombre}{article.proceso ? ` (${article.proceso})` : ''}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {productType === 'recipe' ? (
            <div className="form-group full-width">
              <label className="form-label">Receta</label>
              <select
                className="form-select"
                name="producto_id"
                value={formValues.producto_id}
                onChange={handleChange}
                required
              >
                <option value="">Seleccionar receta</option>
                {options.recipes.map((recipe) => (
                  <option key={recipe.id} value={recipe.id}>
                    {recipe.nombre}{recipe.proceso ? ` (${recipe.proceso})` : ''}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="form-group">
            <label className="form-label">Tipo de excepción</label>
            <div className="radio-group">
              <label className="radio-item">
                <input
                  type="radio"
                  name="tipo_excepcion"
                  value="24h"
                  checked={formValues.tipo_excepcion === '24h'}
                  onChange={handleChange}
                />
                <span className="radio-mark" />
                <span className="radio-label">Disponible 24/7</span>
              </label>
              <label className="radio-item">
                <input
                  type="radio"
                  name="tipo_excepcion"
                  value="custom"
                  checked={formValues.tipo_excepcion === 'custom'}
                  onChange={handleChange}
                />
                <span className="radio-mark" />
                <span className="radio-label">Horarios personalizados</span>
              </label>
            </div>
          </div>

          <div className="form-group full-width">
            <label className="form-label">Motivo</label>
            <textarea
              className="form-input"
              name="motivo"
              value={formValues.motivo}
              onChange={handleChange}
              rows={3}
              placeholder="Describe por qué esta sucursal necesita esta excepción..."
            />
          </div>

          <div className="form-group full-width">
            <label className="form-label">Notas</label>
            <textarea
              className="form-input"
              name="notas"
              value={formValues.notas}
              onChange={handleChange}
              rows={2}
              placeholder="Información adicional opcional..."
            />
          </div>

          <div className="form-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                name="activo"
                checked={formValues.activo}
                onChange={handleChange}
              />
              <span className="checkmark" />
              <span className="checkbox-label">Excepción activa</span>
            </label>
          </div>

          {options.error ? (
            <div className="form-error">{options.error}</div>
          ) : null}

          <div className="modal-footer">
            <button className="btn-secondary" type="button" onClick={onClose} disabled={state.saving}>
              Cancelar
            </button>
            <button className="btn-primary" type="submit" disabled={state.saving || options.loading}>
              {state.saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OperatingExceptionsSection;
